import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { ReportHeader } from '@/components/reports/ReportUI';
import { ReportFilterBar, useReportFilters } from '@/components/reports/ReportFilterBar';
import { ExportTrio } from '@/components/accounting/ExportTrio';
import { Building2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import api from '@/lib/api';
import { toast } from 'sonner';

const COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#f59e0b'];

export default function CostCenterReportPage() {
  const [data, setData] = useState(null);
  const f = useReportFilters({ withUnassigned: true });
  const dateFilter = f.range;
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFilter.start) params.start_date = dateFilter.start;
      if (dateFilter.end) params.end_date = dateFilter.end;
      if (f.branchIds.length) params.branch_ids = f.branchIds.join(',');
      const res = await api.get('/accounting/cost-center-report', { params });
      setData(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [f.params]);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="cost-center-report-page">
        <ReportHeader icon={Building2} title="Cost Center Report" subtitle="Expenses and costs grouped by branch/cost center" testId="cc-header" />
        <ReportFilterBar testId="cc-filter" branches={f.branches} branchIds={f.branchIds} onBranchIds={f.setBranchIds}
          range={f.range} onRange={f.setRange}>
          <ExportTrio spec={() => ({ title: 'Cost Center Report', subtitle: `${f.range.start} → ${f.range.end} · ${f.branchLabel}`, filename: 'cost_center_report', rows: data?.cost_centers || [], cols: [{ key: 'cost_center', label: 'Cost Center' }, { key: 'transaction_count', label: 'Transactions', numeric: true }, { key: 'total', label: 'Total', numeric: true }] })} disabled={!data} testId="cc-export" />
        </ReportFilterBar>

        {data && (
          <div className="grid grid-cols-2 gap-4">
            <Card><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Cost Centers</p><p className="text-2xl font-bold">{data.cost_centers?.length || 0}</p></CardContent></Card>
            <Card><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Grand Total</p><p className="text-2xl font-bold text-error">{cur()} {data.grand_total?.toLocaleString()}</p></CardContent></Card>
          </div>
        )}

        {data?.cost_centers?.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardContent className="pt-5">
                <h3 className="font-semibold mb-4">Cost Distribution</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.cost_centers}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="cost_center" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => `${cur()} ${Number(v).toLocaleString()}`} />
                    <Bar dataKey="total" fill="#f97316" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <h3 className="font-semibold mb-4">Share by Center</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={data.cost_centers} dataKey="total" nameKey="cost_center" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {data.cost_centers.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `${cur()} ${Number(v).toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        )}

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b"><tr>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Cost Center</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Transactions</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Total</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Top Categories</th>
                </tr></thead>
                <tbody>
                  {loading ? <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">Loading...</td></tr> :
                    data?.cost_centers?.map((c, i) => (
                    <tr key={i} className="border-b hover:bg-muted/50">
                      <td className="px-4 py-3 font-medium">{c.cost_center}</td>
                      <td className="px-4 py-3 text-right text-muted-foreground">{c.transaction_count}</td>
                      <td className="px-4 py-3 text-right font-bold text-error">{cur()} {c.total?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{c.categories?.slice(0, 3).map(cat => `${cat.name}: ${cur()} ${cat.amount.toLocaleString()}`).join(' | ')}</td>
                    </tr>
                  ))}
                  {!loading && (!data?.cost_centers || data.cost_centers.length === 0) && <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">No cost center data</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
